import { useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { calendarDate } from '../../shared/dateTime';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { menuLabel } from '../menuConfig';
import { FeaturesLoading, useFeatureState } from '../featureFlags/FeatureGate';
import {
  FEATURE_TRUCK_BANS,
  fetchTruckBanCalendar,
  weekdayLabel,
  type TruckBanCalendarData,
} from './truckBansApi';

/**
 * „Kalendarz zakazów” (19.10.2026; odpowiedź Marathonu 16.10.2026 pyt. 11,
 * przełącznik `truckBanCalendar`) — podgląd tylko do odczytu.
 *
 * Wiersze zasiane z aktów prawnych krajów, każdy z podstawą i linkiem.
 * Zmiana kalendarza (np. dekret IT na nowy rok) to nowa migracja — ekranu
 * edycji nie ma. Rekomendacje pokazują z tego ostrzeżenie przy zleceniu.
 */

export const FEATURE_OFF_NOTE =
  'Ekran „Kalendarz zakazów” jest wyłączony w wersji aplikacji, z której korzystasz.';

export const INTRO_NOTE =
  'Zakazy ruchu ciężarówek z przepisów krajowych — każdy wiersz z podstawą prawną i linkiem do źródła. Godziny są czasem lokalnym kraju (wszystkie kraje kalendarza mają ten sam czas co Polska). Landy, kantony i odcinki dróg są tylko opisane, a wyjątki (np. świeże produkty, transport kombinowany) — wypisane tekstem. Rekomendacje ostrzegają, gdy okno załadunku albo rozładunku nachodzi na zakaz w kraju punktu; przejazdu przez inne kraje nie sprawdzamy.';

export const TruckBanCalendar = () => {
  const on = useFeatureState(FEATURE_TRUCK_BANS);
  if (on === undefined) return <FeaturesLoading />;
  if (!on) {
    return (
      <Box sx={{ p: 2 }}>
        <Title title={menuLabel('truck-bans')} />
        <Alert severity="info">{FEATURE_OFF_NOTE}</Alert>
      </Box>
    );
  }
  return <TruckBanCalendarScreen />;
};

const hours = (a: string, b: string) => `${a}–${b}`;

const SourceLink = ({ url, label }: { url: string | null; label: string | null }) =>
  url ? (
    <Link href={url} target="_blank" rel="noopener noreferrer">
      {label ?? 'źródło'}
    </Link>
  ) : (
    <>—</>
  );

const TruckBanCalendarScreen = () => {
  const [data, setData] = useState<TruckBanCalendarData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [country, setCountry] = useState('');

  useEffect(() => {
    let alive = true;
    fetchTruckBanCalendar()
      .then((d) => {
        if (!alive) return;
        setData(d);
        setCountry((c) => c || d.countries.find((x) => x.generalBan)?.country || '');
      })
      .catch((e) => {
        if (alive) setError(toErrorMessage(e));
      });
    return () => {
      alive = false;
    };
  }, []);

  const rules = useMemo(() => (data?.rules ?? []).filter((r) => r.country === country), [data, country]);
  const days = useMemo(() => (data?.days ?? []).filter((d) => d.country === country), [data, country]);

  return (
    <Box sx={{ p: 2 }}>
      <Title title={menuLabel('truck-bans')} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {INTRO_NOTE}
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <CircularProgress size={24} />}
      {data && (
        <>
          <Typography variant="h6">Kraje</Typography>
          <Table size="small" aria-label="Kraje" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Kraj</TableCell>
                <TableCell>Zakaz</TableCell>
                <TableCell>Kalendarz obejmuje</TableCell>
                <TableCell>Czego nie obejmuje</TableCell>
                <TableCell>Źródło</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.countries.map((c) => (
                <TableRow key={c.country} data-testid={`country-${c.country}`}>
                  <TableCell>
                    {c.country} — {c.name}
                  </TableCell>
                  <TableCell>{c.generalBan ? c.summary : 'brak ogólnego zakazu'}</TableCell>
                  <TableCell>
                    {calendarDate(c.coveredFrom)} – {calendarDate(c.coveredTo)}
                  </TableCell>
                  <TableCell>{c.notCovered ?? '—'}</TableCell>
                  <TableCell>
                    <SourceLink url={c.sourceUrl} label={c.legalBasis} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TextField
            select
            size="small"
            label="Kraj"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            sx={{ minWidth: 220, mb: 1 }}
            slotProps={{ htmlInput: { 'data-testid': 'country-select' } }}
          >
            {data.countries
              .filter((c) => c.generalBan)
              .map((c) => (
                <MenuItem key={c.country} value={c.country}>
                  {c.country} — {c.name}
                </MenuItem>
              ))}
          </TextField>

          <Typography variant="h6">Zakazy tygodniowe</Typography>
          {rules.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Brak zakazów tygodniowych dla tego kraju.
            </Typography>
          ) : (
            <Table size="small" aria-label="Zakazy tygodniowe" sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Dzień</TableCell>
                  <TableCell>Godziny</TableCell>
                  <TableCell>Okres</TableCell>
                  <TableCell>Gdzie</TableCell>
                  <TableCell>Pojazdy i wyjątki</TableCell>
                  <TableCell>Podstawa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id} data-testid={`rule-${r.id}`}>
                    <TableCell>{weekdayLabel(r.weekday)}</TableCell>
                    <TableCell>{hours(r.startTime, r.endTime)}</TableCell>
                    <TableCell>
                      {r.validFrom || r.validTo
                        ? `${calendarDate(r.validFrom)} – ${calendarDate(r.validTo)}`
                        : 'cały rok'}
                    </TableCell>
                    <TableCell>{r.scope}</TableCell>
                    <TableCell>
                      {r.vehicles}
                      {r.exceptions && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {r.exceptions}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <SourceLink url={r.sourceUrl} label={r.legalBasis} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography variant="h6">Święta i dni szczególne</Typography>
          {days.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Brak dni szczególnych dla tego kraju.
            </Typography>
          ) : (
            <Table size="small" aria-label="Święta i dni szczególne">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Dzień</TableCell>
                  <TableCell>Godziny</TableCell>
                  <TableCell>Gdzie</TableCell>
                  <TableCell>Podstawa</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map((d) => (
                  <TableRow key={d.id} data-testid={`day-${d.id}`}>
                    <TableCell>{calendarDate(d.date)}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{hours(d.startTime, d.endTime)}</TableCell>
                    <TableCell>
                      {d.scope}
                      {d.regions && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {d.regions}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <SourceLink url={d.sourceUrl} label={d.legalBasis} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </Box>
  );
};
